/**
 * API Logger module for TimeSeries application
 * Provides functionality to log API requests and responses
 */

// Define log levels
export enum LogLevel {
  INFO = 'INFO',
  WARNING = 'WARNING',
  ERROR = 'ERROR'
}

// Logger interface
export interface ApiLogger {
  log(level: LogLevel, message: string, data?: Record<string, unknown>): void;
}

// Log entry interface
export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: Record<string, unknown>;
}

/**
 * Console API logger implementation
 */
class ConsoleApiLogger implements ApiLogger {
  private logs: LogEntry[] = [];
  private readonly maxLogs: number = 100;

  /**
   * Log a message with associated data
   */
  log(level: LogLevel, message: string, data?: Record<string, unknown>): void {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      data
    };
    
    // Add to memory logs
    this.logs.push(entry);
    
    // Trim logs if exceeding max capacity
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }
    
    // Log to console
    switch (level) {
      case LogLevel.ERROR:
        console.error(`[API ERROR] ${message}`, data || '');
        break;
      case LogLevel.WARNING:
        console.warn(`[API WARNING] ${message}`, data || '');
        break;
      case LogLevel.INFO:
        console.info(`[API INFO] ${message}`, data || '');
        break;
    }
    
    // Store in local storage for persistence
    this.saveToLocalStorage();
  }
  
  /**
   * Save logs to local storage
   */
  private saveToLocalStorage(): void {
    try {
      localStorage.setItem('api_logs', JSON.stringify(this.logs));
    } catch (error) {
      // Local storage may be unavailable in some contexts
      console.warn('Failed to save logs to localStorage', error);
    }
  }
  
  /**
   * Get all logs
   */
  getLogs(): LogEntry[] {
    return [...this.logs];
  }
  
  /**
   * Clear all logs
   */
  clearLogs(): void {
    this.logs = [];
    localStorage.removeItem('api_logs');
  }
  
  /**
   * Load logs from local storage
   */
  loadFromLocalStorage(): void {
    try {
      const storedLogs = localStorage.getItem('api_logs');
      if (storedLogs) {
        this.logs = JSON.parse(storedLogs);
      }
    } catch (error) {
      console.warn('Failed to load logs from localStorage', error);
    }
  }
}

// Create a singleton instance
export const apiLogger = new ConsoleApiLogger();

// Initialize logger from local storage if available
try {
  apiLogger.loadFromLocalStorage();
} catch {
  // Silently fail if localStorage is not available
}